import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Search, Mountain, ClipboardCheck, ArrowRight } from 'lucide-react';
type Step = {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
};
const steps: Step[] = [{
  number: '1',
  title: 'Connection Call',
  description: 'Start the conversation',
  icon: <MessageCircle size={24} strokeWidth={1.5} data-id="element-76" />
}, {
  number: '2',
  title: 'Life Scan',
  description: 'See where you are now',
  icon: <Search size={24} strokeWidth={1.5} data-id="element-77" />
}, {
  number: '3',
  title: 'Future Focus',
  description: 'Define where you want to go',
  icon: <Mountain size={24} strokeWidth={1.5} data-id="element-78" />
}, {
  number: '4',
  title: 'Clarity Roadmap',
  description: 'Create your next steps',
  icon: <ClipboardCheck size={24} strokeWidth={1.5} data-id="element-79" />
}];
export function JourneySection() {
  return <section className="relative py-24 overflow-hidden" data-id="element-80">
      <div className="absolute inset-0 bg-grain opacity-15 pointer-events-none mix-blend-overlay" data-id="element-81"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-82">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-83">
          <p className="text-cream-50/90 text-base md:text-lg leading-relaxed max-w-3xl mx-auto mb-8" data-id="element-84">
            Mark created a 4-step coaching model, based on ICF principles, to
            help you step back from the pressure of daily life, regain ownership
            of your choices, and create your own direction.
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-gold-400 mb-4 text-glow" data-id="element-85">
            Your 4-Step Journey
          </h2>
          <div className="flex items-center justify-center gap-2 mb-5" data-id="element-86">
            <span className="h-px w-12 bg-gold-400/40" data-id="element-87"></span>
            <span className="w-1.5 h-1.5 rounded-full bg-gold-400/60" data-id="element-88"></span>
            <span className="h-px w-12 bg-gold-400/40" data-id="element-89"></span>
          </div>
          <p className="text-cream-50/80 text-lg max-w-2xl mx-auto" data-id="element-90">
            Move from feeling stuck to clarity and direction.
          </p>
        </motion.div>

        {/* Horizontal Flowchart - scrolls on small screens */}
        <div className="overflow-x-auto pb-6 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide" data-id="element-91">
          <div className="flex flex-row items-center justify-start lg:justify-center gap-2 sm:gap-3 min-w-max lg:min-w-0" data-id="element-92">
            {/* Starting Point A */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="flex flex-col items-center gap-2 flex-shrink-0 w-[90px]" data-id="element-93">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-night-800 border-2 border-gold-400/40 flex items-center justify-center font-serif text-xl sm:text-2xl text-gold-400" data-id="element-94">
                A
              </div>
              <span className="text-cream-50/70 text-xs sm:text-sm font-medium text-center" data-id="element-95">
                Feeling stuck
              </span>
            </motion.div>

            {/* Dotted connector */}
            <div className="flex items-center self-center pt-0 sm:-mt-7 flex-shrink-0" data-id="element-96">
              <div className="w-4 sm:w-6 border-t-2 border-dotted border-gold-400/40" data-id="element-97"></div>
            </div>

            {/* Steps with arrows between */}
            {steps.map((step, index) => <Fragment key={step.number} data-id="element-98">
                <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1 + index * 0.15,
              ease: 'easeOut'
            }} className="relative flex-shrink-0 w-[180px] sm:w-[200px] lg:w-[210px]" data-id="element-99">
                  {/* Numbered Badge */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20" data-id="element-100">
                    <div className="w-9 h-9 rounded-full bg-gold-400 text-night-900 font-serif font-bold flex items-center justify-center shadow-lg" data-id="element-101">
                      {step.number}
                    </div>
                  </div>

                  {/* Card */}
                  <div className="h-full bg-night-800/50 backdrop-blur-sm border border-gold-400/20 rounded-2xl pt-8 pb-5 px-4 flex flex-col items-center text-center transition-all duration-300 hover:border-gold-400/50 hover:bg-night-800/70 hover:-translate-y-1" data-id="element-102">
                    <div className="w-12 h-12 rounded-full bg-gold-400/10 border border-gold-400/30 flex items-center justify-center text-gold-400 mb-3" data-id="element-103">
                      {step.icon}
                    </div>
                    <h3 className="font-serif text-lg sm:text-xl text-cream-50 mb-2" data-id="element-104">
                      {step.title}
                    </h3>
                    <div className="w-8 h-px bg-gold-400/40 mb-2" data-id="element-105"></div>
                    <p className="text-cream-50/70 text-xs sm:text-sm leading-relaxed" data-id="element-106">
                      {step.description}
                    </p>
                  </div>
                </motion.div>

                {/* Arrow between cards (not after last) */}
                {index < steps.length - 1 && <motion.div initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: 0.25 + index * 0.15
            }} className="flex items-center self-center flex-shrink-0" data-id="element-107">
                    <ArrowRight size={20} className="text-gold-400/70" strokeWidth={1.5} data-id="element-108" />
                  </motion.div>}
              </Fragment>)}

            {/* Arrow before B */}
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.85
          }} className="flex items-center self-center flex-shrink-0" data-id="element-109">
              <ArrowRight size={20} className="text-gold-400" strokeWidth={1.5} data-id="element-110" />
            </motion.div>

            {/* Ending Point B */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.95
          }} className="flex flex-col items-center gap-2 flex-shrink-0 w-[90px]" data-id="element-111">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gold-400 border-2 border-gold-400 flex items-center justify-center font-serif text-xl sm:text-2xl text-night-900 font-bold shadow-[0_0_20px_rgba(244,196,48,0.4)]" data-id="element-112">
                B
              </div>
              <span className="text-gold-400 text-xs sm:text-sm font-medium text-center" data-id="element-113">
                Clarity & direction
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}